import { getEmbeddedAuthServerTime } from "../../../src/lib/auth/embeddedAuthClock";
import { contrastingTextColor, DEFAULT_ANNOUNCEMENT_STYLE } from "./announcement-colors";
import { createPostDialog } from "./post-dialog";
import { isPostPresentation, legacyPostSize } from "../../../src/lib/posts/presentation";
import { createPostNotifications, parsePostState } from "./posts";
import { renewWidgetEmbedProof } from "./embed-proof";

type WidgetInitOptions = {
  clientId?: string;
  clientName?: string;
  origin?: string;
  widgetPath?: string;
  hideLauncher?: boolean;
};

type WidgetSetClientOptions = {
  clientId?: string;
  clientName?: string;
};

type WidgetAuthToken = {
  customToken: string;
  expiresAt: string;
  serverTime?: string;
};

type WidgetAuthTokenProvider = () => Promise<WidgetAuthToken>;

type WidgetSetOpenOptions = {
  open?: boolean;
};

type WidgetSetVisibilityOptions = {
  visible?: boolean;
};

type AnnouncementLayout = "inline" | "floating";

type AnnouncementPosition = "top" | "bottom";

type AnnouncementContentAlignment = "left" | "center" | "right";

type AnnouncementCta = {
  label: string;
  appearance: "link" | "button";
  dismissOnClick: boolean;
} & (
  | { type: "open_url"; url: string; openIn: "same_tab" | "new_tab" }
  | { type: "start_chat"; initialMessage: string }
);

type WidgetAnnouncement = {
  id: string;
  title: string;
  body: string;
  layout: AnnouncementLayout;
  position: AnnouncementPosition;
  contentAlignment: AnnouncementContentAlignment;
  style: { backgroundColor: string; ctaButtonColor: string };
  cta: AnnouncementCta | null;
  dismissible: boolean;
  revision: number;
};

type WidgetAnnouncementDismissOptions = {
  announcementId: string;
  revision: number;
};

type WidgetAnnouncementCtaResultPostMessage = {
  type: "nest-support-widget:announcement:cta:result";
  requestId: string;
  chat?: { status: "success"; dismissed: boolean } | { status: "cancelled" | "error" };
};

const DEFAULT_WIDGET_PATH = "/widget";
const SOUND_ENABLED_STORAGE_KEY = "nest-support-widget:soundEnabled";
const SOUND_VOLUME_STORAGE_KEY = "nest-support-widget:soundVolume";
const PANEL_SIZE_STORAGE_KEY = "nest-support-widget:panelSize";
const STATE_EVENT_NAME = "nest-support-widget:state";
const SALES_HOSTS = new Set<string>([
  "dev.nest.botlogy.com",
  "staging.nest.botlogy.com",
  "preview.nest.botlogy.com",
  "nest.botlogy.com",
]);
const SUPPORT_HOSTS = new Set<string>([
  "console.botlogy.com",
  "dev.console.botlogy.com",
  "staging.console.botlogy.com",
  "dev.opshub.botlogy.com",
  "staging.opshub.botlogy.com",
  "opshub.botlogy.com",
]);

type PanelSize = "default" | "large";

type WidgetState = {
  open: boolean;
  visible: boolean;
  panelSize: PanelSize;
  unreadCount: number;
};

type WidgetStateListener = (state: WidgetState) => void;

type WidgetStateRequestPostMessage = {
  type: "nest-support-widget:state:request";
};

type WidgetStatePostMessage = WidgetState & {
  postDialog?: boolean;
  postCloseGuard?: boolean;
  type: "nest-support-widget:state";
};

type UnreadPostMessage = {
  type: "nest-support-widget:unread";
  unreadCount: number;
  conversationId?: string;
};

type AnnouncementsPostMessage = {
  type: "nest-support-widget:announcements";
  clientId: string | null;
  announcements: WidgetAnnouncement[];
};

type SettingsRequestPostMessage = {
  type: "nest-support-widget:settings:request";
};

type SettingsUpdatePostMessage = {
  type: "nest-support-widget:settings:update";
  soundEnabled?: boolean;
  soundVolume?: number;
  panelSize?: PanelSize;
};

type SoundTestPostMessage = {
  type: "nest-support-widget:sound:test";
};

type SettingsDialogControlPostMessage =
  | { type: "nest-support-widget:settings:dialog:open" }
  | { type: "nest-support-widget:settings:dialog:close" };

type SettingsDialogStatePostMessage = {
  type: "nest-support-widget:settings:dialog:state";
  open: boolean;
};

type AuthTokenRequestPostMessage = {
  type: "nest-support-widget:auth-token:request";
  messageId: string;
};

type UnreadState = {
  initialized: boolean;
  lastUnreadCount: number;
};

let unreadState: UnreadState = { initialized: false, lastUnreadCount: 0 };
let postUnreadCount = 0;
let postPanelWasOpen: boolean | null = null;
type WidgetPostGuard = { clientId: string; postId: string; sessionId: string };
let widgetPostGuard: WidgetPostGuard | null = null;
let widgetPostClose: { requestId: string; context: WidgetPostGuard; done: () => void } | null = null;
function requestWidgetPostClose(done: () => void): boolean {
  const frame = document.getElementById("nest-support-widget-iframe");
  if (!widgetPostGuard || !mountedState || !(frame instanceof HTMLIFrameElement)) return false;
  if (widgetPostClose) return true;
  const requestId = crypto.randomUUID();
  widgetPostClose = { requestId, context: widgetPostGuard, done };
  frame.contentWindow?.postMessage({ type: "nest-support-widget:posts:close:request", ...widgetPostGuard, requestId }, mountedState.origin);
  return true;
}
const postNotifications = createPostNotifications({
  send: (message) => {
    const iframe = document.getElementById("nest-support-widget-iframe");
    if (mountedState && iframe instanceof HTMLIFrameElement) iframe.contentWindow?.postMessage(message, mountedState.origin);
  },
  available: () => widgetVisible,
  panelOpen: () => panelOpen || postDialog.isOpen(),
  onUnread: (count) => { const changed = postUnreadCount !== count; postUnreadCount = count; updateBadge(unreadState.lastUnreadCount); if (changed) emitWidgetState(); },
});
const postDialog = createPostDialog({
  getAuthProvider: () => authTokenProvider,
  sendWidget: (message) => {
    const frame = document.getElementById("nest-support-widget-iframe");
    if (mountedState && frame instanceof HTMLIFrameElement) frame.contentWindow?.postMessage(message, mountedState.origin);
  },
  onClose: () => postNotifications.refresh(),
});
function refreshPostNotifications() { window.setTimeout(() => postNotifications.refresh(), 0); }
let panelOpen = false;

let soundEnabled = readSoundEnabledFromStorage();
let soundVolume = readSoundVolumeFromStorage();
let panelSize: PanelSize = readPanelSizeFromStorage();
let widgetVisible = true;
let lastSoundAt = 0;
let audioContext: AudioContext | null = null;
let windowMessageHandler: ((event: MessageEvent) => void) | null = null;
let cancelEmbedProofRenewal: (() => void) | null = null;
let isWindowMessageHandlerBound = false;
let isAudioUnlockBound = false;
let authTokenProvider: WidgetAuthTokenProvider | null = null;
let pendingAuthTokenRequestId: string | null = null;
let shouldPostWidgetStateToIframe = false;
let inFlightAuthTokenRequest:
  | {
      messageId: string;
      provider: WidgetAuthTokenProvider;
    }
  | null = null;
const widgetStateListeners = new Set<WidgetStateListener>();
let announcementState: WidgetAnnouncement[] = [];
let announcementBannerResizeObserver: ResizeObserver | null = null;
let announcementHostResizeHandler: (() => void) | null = null;
let announcementBannerElement: HTMLDivElement | null = null;
let pendingAnnouncementCtaNavigation: { requestId: string; url: string; timeoutId: number } | null = null;
let pendingAnnouncementChat: { requestId: string; announcement: WidgetAnnouncement } | null = null;
let announcementChatError: { id: string; revision: number } | null = null;

const ANNOUNCEMENT_BANNER_ATTRIBUTE = "data-nest-support-announcement-banner";
const ANNOUNCEMENT_BANNER_SELECTOR = `[${ANNOUNCEMENT_BANNER_ATTRIBUTE}="1"]`;
const ANNOUNCEMENT_LAYOUT_ATTRIBUTE = "data-nest-support-announcement-layout";
const ANNOUNCEMENT_POSITION_ATTRIBUTE = "data-nest-support-announcement-position";
const ANNOUNCEMENT_OFFSET_PROPERTY = "--nest-support-announcement-offset";
const ANNOUNCEMENT_HOST_PADDING_TOP_PROPERTY = "--nest-support-announcement-host-padding-top";
const ANNOUNCEMENT_HOST_PADDING_BOTTOM_PROPERTY = "--nest-support-announcement-host-padding-bottom";
const ANNOUNCEMENT_CTA_NAVIGATION_TIMEOUT_MS = 1_500;

function normalizeOrigin(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    if (!/^https?:$/.test(url.protocol)) return null;
    return url.origin;
  } catch {
    return null;
  }
}

type MountedState = Required<Pick<WidgetInitOptions, "origin" | "widgetPath" | "hideLauncher">> &
  Pick<WidgetInitOptions, "clientId" | "clientName">;
let mountedState: MountedState | null = null;

function normalizeOptionalString(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const trimmed = v.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function parseOptionalBoolean(v: unknown): boolean | undefined {
  if (typeof v !== "string") return undefined;
  const trimmed = v.trim().toLowerCase();
  if (trimmed === "1" || trimmed === "true" || trimmed === "yes") return true;
  if (trimmed === "0" || trimmed === "false" || trimmed === "no") return false;
  return undefined;
}

function isWidgetHostAllowed(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  return SALES_HOSTS.has(host) || SUPPORT_HOSTS.has(host);
}

function assertAllowedHost(): void {
  if (typeof process !== "undefined" && process.env.NODE_ENV === "test") return;
  const host = window.location.hostname;
  if (!isWidgetHostAllowed(host)) {
    throw new Error(`NestSupportWidget: 埋め込み元ドメインが許可されていません (${host})`);
  }
}

function readSoundEnabledFromStorage(): boolean {
  try {
    const v = window.localStorage.getItem(SOUND_ENABLED_STORAGE_KEY);
    if (v === null) return true;
    if (v === "1" || v === "true") return true;
    if (v === "0" || v === "false") return false;
    return true;
  } catch {
    return true;
  }
}

function readSoundVolumeFromStorage(): number {
  try {
    const v = window.localStorage.getItem(SOUND_VOLUME_STORAGE_KEY);
    if (v === null) return 100;
    const n = Number(v);
    return clampSoundVolume(n);
  } catch {
    return 100;
  }
}

function readPanelSizeFromStorage(): PanelSize {
  try {
    const v = window.localStorage.getItem(PANEL_SIZE_STORAGE_KEY);
    return v === "large" ? "large" : "default";
  } catch {
    return "default";
  }
}

function writeSoundEnabledToStorage(enabled: boolean): void {
  try {
    window.localStorage.setItem(SOUND_ENABLED_STORAGE_KEY, enabled ? "1" : "0");
  } catch {
    // noop
  }
}

function writeSoundVolumeToStorage(volume: number): void {
  try {
    window.localStorage.setItem(SOUND_VOLUME_STORAGE_KEY, String(clampSoundVolume(volume)));
  } catch {
    // noop
  }
}

function writePanelSizeToStorage(size: PanelSize): void {
  try {
    window.localStorage.setItem(PANEL_SIZE_STORAGE_KEY, size);
  } catch {
    // noop
  }
}

function clampSoundVolume(v: number): number {
  const n = Math.round(v);
  if (!Number.isFinite(n)) return 100;
  return Math.max(0, Math.min(100, n));
}

function isPanelSize(v: unknown): v is PanelSize {
  return v === "default" || v === "large";
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

function createMessageId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function parseUnreadPostMessage(data: unknown): UnreadPostMessage | null {
  if (!isRecord(data)) return null;
  if (data.type !== "nest-support-widget:unread") return null;
  const unreadCount = data.unreadCount;
  if (typeof unreadCount !== "number" || !Number.isFinite(unreadCount)) return null;
  const conversationId = typeof data.conversationId === "string" ? data.conversationId : undefined;
  return { type: "nest-support-widget:unread", unreadCount, conversationId };
}

function parseAnnouncementsPostMessage(data: unknown): AnnouncementsPostMessage | null {
  if (!isRecord(data)) return null;
  if (data.type !== "nest-support-widget:announcements" || !Array.isArray(data.announcements)) return null;
  if (data.clientId !== null && (typeof data.clientId !== "string" || data.clientId.trim().length === 0)) return null;
  const announcements: WidgetAnnouncement[] = [];
  for (const raw of data.announcements) {
    if (!isRecord(raw)) return null;
    if (typeof raw.id !== "string" || raw.id.trim().length === 0) return null;
    if (typeof raw.title !== "string" || typeof raw.body !== "string") return null;
    if (raw.layout !== "inline" && raw.layout !== "floating") return null;
    if (raw.position !== "top" && raw.position !== "bottom") return null;
    const contentAlignment =
      raw.contentAlignment === "left" || raw.contentAlignment === "center" || raw.contentAlignment === "right"
        ? raw.contentAlignment
        : "left";
    let style = DEFAULT_ANNOUNCEMENT_STYLE;
    if (raw.style !== undefined) {
      if (!isRecord(raw.style)) return null;
      const { backgroundColor, ctaButtonColor } = raw.style;
      if (typeof backgroundColor !== "string" || !/^#[0-9a-fA-F]{6}$/.test(backgroundColor)) return null;
      if (typeof ctaButtonColor !== "string" || !/^#[0-9a-fA-F]{6}$/.test(ctaButtonColor)) return null;
      style = { backgroundColor, ctaButtonColor };
    }
    let cta: AnnouncementCta | null = null;
    if (raw.cta !== undefined && raw.cta !== null) {
      if (!isRecord(raw.cta)) return null;
      if (raw.cta.type !== "open_url" && raw.cta.type !== "start_chat") return null;
      if (typeof raw.cta.label !== "string" || raw.cta.label.trim().length === 0) return null;
      if (raw.cta.appearance !== "link" && raw.cta.appearance !== "button") return null;
      if (typeof raw.cta.dismissOnClick !== "boolean") return null;
      if (raw.cta.type === "start_chat") {
        if (typeof raw.cta.initialMessage !== "string") return null;
        cta = { type: "start_chat", label: raw.cta.label, initialMessage: raw.cta.initialMessage, appearance: raw.cta.appearance, dismissOnClick: raw.cta.dismissOnClick };
      } else {
        if (typeof raw.cta.url !== "string" || raw.cta.url.trim().length === 0) return null;
        if (raw.cta.openIn !== "same_tab" && raw.cta.openIn !== "new_tab") return null;
        cta = {
        type: "open_url",
        label: raw.cta.label,
        url: raw.cta.url,
        appearance: raw.cta.appearance,
        openIn: raw.cta.openIn,
        dismissOnClick: raw.cta.dismissOnClick,
      };
      }
    }
    if (typeof raw.dismissible !== "boolean") return null;
    if (typeof raw.revision !== "number" || !Number.isInteger(raw.revision) || raw.revision <= 0) return null;
    announcements.push({
      id: raw.id,
      title: raw.title,
      body: raw.body,
      layout: raw.layout,
      position: raw.position,
      contentAlignment,
      style,
      cta,
      dismissible: raw.dismissible,
      revision: raw.revision,
    });
  }
  return { type: "nest-support-widget:announcements", clientId: data.clientId, announcements };
}

function parseAnnouncementCtaResultPostMessage(data: unknown): WidgetAnnouncementCtaResultPostMessage | null {
  if (!isRecord(data)) return null;
  if (data.type !== "nest-support-widget:announcement:cta:result") return null;
  if (typeof data.requestId !== "string" || data.requestId.trim().length === 0) return null;
  let chat: WidgetAnnouncementCtaResultPostMessage["chat"];
  if (data.chat !== undefined) {
    if (!isRecord(data.chat)) return null;
    if (data.chat.status === "success" && typeof data.chat.dismissed === "boolean") chat = { status: "success", dismissed: data.chat.dismissed };
    else if (data.chat.status === "error" || data.chat.status === "cancelled") chat = { status: data.chat.status };
    else return null;
  }
  return { type: "nest-support-widget:announcement:cta:result", requestId: data.requestId, ...(chat ? { chat } : {}) };
}

function parseSettingsRequestPostMessage(data: unknown): SettingsRequestPostMessage | null {
  if (!isRecord(data)) return null;
  if (data.type !== "nest-support-widget:settings:request") return null;
  return { type: "nest-support-widget:settings:request" };
}

function parseSettingsUpdatePostMessage(data: unknown): SettingsUpdatePostMessage | null {
  if (!isRecord(data)) return null;
  if (data.type !== "nest-support-widget:settings:update") return null;
  const soundEnabledValue = data.soundEnabled;
  const soundVolumeValue = data.soundVolume;
  const hasSoundEnabled = Object.prototype.hasOwnProperty.call(data, "soundEnabled");
  const hasSoundVolume = Object.prototype.hasOwnProperty.call(data, "soundVolume");
  const panelSizeValue = data.panelSize;
  const hasPanelSize = Object.prototype.hasOwnProperty.call(data, "panelSize");

  if (!hasSoundEnabled && !hasSoundVolume && !hasPanelSize) return null;
  if (hasSoundEnabled && typeof soundEnabledValue !== "boolean") return null;
  if (hasSoundVolume && (typeof soundVolumeValue !== "number" || !Number.isFinite(soundVolumeValue))) return null;
  if (hasPanelSize && !isPanelSize(panelSizeValue)) return null;

  return {
    type: "nest-support-widget:settings:update",
    soundEnabled: hasSoundEnabled ? (soundEnabledValue as boolean) : undefined,
    soundVolume: hasSoundVolume ? (soundVolumeValue as number) : undefined,
    panelSize: hasPanelSize ? (panelSizeValue as PanelSize) : undefined,
  };
}

function parseSoundTestPostMessage(data: unknown): SoundTestPostMessage | null {
  if (!isRecord(data)) return null;
  if (data.type !== "nest-support-widget:sound:test") return null;
  return { type: "nest-support-widget:sound:test" };
}

function parseSettingsDialogStatePostMessage(data: unknown): SettingsDialogStatePostMessage | null {
  if (!isRecord(data)) return null;
  if (data.type !== "nest-support-widget:settings:dialog:state") return null;
  if (typeof data.open !== "boolean") return null;
  return { type: "nest-support-widget:settings:dialog:state", open: data.open };
}

function parseAuthTokenRequestPostMessage(data: unknown): AuthTokenRequestPostMessage | null {
  if (!isRecord(data)) return null;
  if (data.type !== "nest-support-widget:auth-token:request") return null;
  if (typeof data.messageId !== "string" || data.messageId.trim().length === 0) return null;
  return { type: "nest-support-widget:auth-token:request", messageId: data.messageId };
}

function parseWidgetStateRequestPostMessage(data: unknown): WidgetStateRequestPostMessage | null {
  if (!isRecord(data)) return null;
  if (data.type !== "nest-support-widget:state:request") return null;
  return { type: "nest-support-widget:state:request" };
}

function formatBadgeText(unreadCount: number): string {
  if (unreadCount > 99) return "99+";
  if (unreadCount > 9) return "9+";
  return String(unreadCount);
}

function updateBadge(unreadCount: number): void {
  const el = document.getElementById("nest-support-widget-badge");
  if (!(el instanceof HTMLSpanElement)) return;

  const normalized = Math.max(0, Math.floor(unreadCount)) + postUnreadCount;
  if (normalized <= 0) {
    el.textContent = "";
    el.style.display = "none";
    return;
  }

  el.textContent = formatBadgeText(normalized);
  el.style.display = "inline-flex";
}

function pulseBadge(): void {
  const el = document.getElementById("nest-support-widget-badge");
  if (!(el instanceof HTMLSpanElement)) return;
  el.classList.remove("nest-support-widget-badge--pulse");
  void el.offsetWidth;
  el.classList.add("nest-support-widget-badge--pulse");
}

function getAudioContextClass(): typeof AudioContext | null {
  const w = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

function getAudioContext(): AudioContext | null {
  const Ctor = getAudioContextClass();
  if (!Ctor) return null;
  if (!audioContext) audioContext = new Ctor();
  return audioContext;
}

async function ensureAudioUnlocked(): Promise<void> {
  const ctx = getAudioContext();
  if (!ctx) return;
  if (ctx.state === "running") return;
  try {
    await ctx.resume();
  } catch {
    // ブラウザの自動再生制限等で resume できない場合は無視（次のユーザー操作で再トライ）
  }
}

function bindAudioUnlockHandlers(): void {
  if (isAudioUnlockBound) return;
  isAudioUnlockBound = true;

  const handler = () => {
    void ensureAudioUnlocked();
  };

  // 代表的な「ユーザー操作」イベントを拾い、初回のみ AudioContext を解放する
  document.addEventListener("pointerdown", handler, { once: true, capture: true });
  document.addEventListener("keydown", handler, { once: true, capture: true });
}

function playNotificationSound(options?: { force?: boolean }): void {
  if (!soundEnabled) return;
  if (soundVolume <= 0) return;

  const now = Date.now();
  // 連続通知で鳴り続けるのを避ける（同一イベントの二重通知もケア）
  if (!options?.force && now - lastSoundAt < 1500) return;
  lastSoundAt = now;

  const ctx = getAudioContext();
  if (!ctx) return;

  // resume は user gesture が無いと失敗し得るが、失敗しても例外で落とさない
  if (ctx.state === "suspended") {
    void ctx.resume().catch(() => {
      // noop
    });
  }

  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = "triangle";

    osc.connect(gain);
    gain.connect(ctx.destination);

    const t = ctx.currentTime;
    const volume = clampSoundVolume(soundVolume);
    const normalized = volume / 100;
    const curved = Math.log10(1 + 9 * normalized);
    const peak = 0.35 * curved;
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.00011, peak), t + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);

    osc.frequency.setValueAtTime(880, t);
    osc.frequency.exponentialRampToValueAtTime(1320, t + 0.06);

    osc.start(t);
    osc.stop(t + 0.3);
  } catch {
    // noop
  }
}

function postSettingsToIframe(iframe: HTMLIFrameElement, origin: string): void {
  const hostUrl = `${window.location.origin}${window.location.pathname}`;
  iframe.contentWindow?.postMessage(
    {
      type: "nest-support-widget:settings",
      soundEnabled,
      soundVolume: clampSoundVolume(soundVolume),
      panelSize,
      hostUrl,
    },
    origin,
  );
}

function postSettingsDialogControlToIframe(
  iframe: HTMLIFrameElement,
  origin: string,
  msg: SettingsDialogControlPostMessage,
): void {
  try {
    iframe.contentWindow?.postMessage(msg, origin);
  } catch {
    // noop
  }
}

function postAuthTokenUnavailableToIframe(iframe: HTMLIFrameElement, origin: string, messageId: string): void {
  try {
    iframe.contentWindow?.postMessage(
      {
        type: "nest-support-widget:auth-token:unavailable",
        messageId,
      },
      origin,
    );
  } catch {
    // noop
  }
}

async function postAuthTokenToIframe(iframe: HTMLIFrameElement, origin: string, messageId: string): Promise<void> {
  const provider = authTokenProvider;
  if (!provider) {
    postAuthTokenUnavailableToIframe(iframe, origin, messageId);
    if (pendingAuthTokenRequestId === messageId) pendingAuthTokenRequestId = null;
    return;
  }

  try {
    const token = await provider();
    if (pendingAuthTokenRequestId !== messageId) return;
    if (authTokenProvider !== provider) return;
    const serverTime = getEmbeddedAuthServerTime(token);
    if (!token.customToken || !token.expiresAt || Number.isNaN(Date.parse(token.expiresAt)) || serverTime === null) {
      postAuthTokenUnavailableToIframe(iframe, origin, messageId);
      if (pendingAuthTokenRequestId === messageId) pendingAuthTokenRequestId = null;
      if (inFlightAuthTokenRequest?.messageId === messageId && inFlightAuthTokenRequest.provider === provider) {
        inFlightAuthTokenRequest = null;
      }
      return;
    }
    iframe.contentWindow?.postMessage(
      {
        type: "nest-support-widget:auth-token:response",
        messageId,
        customToken: token.customToken,
        expiresAt: token.expiresAt,
        issuedAt: new Date(serverTime).toISOString(),
        serverTime: new Date(serverTime).toISOString(),
      },
      origin,
    );
    if (pendingAuthTokenRequestId === messageId) pendingAuthTokenRequestId = null;
    if (inFlightAuthTokenRequest?.messageId === messageId && inFlightAuthTokenRequest.provider === provider) {
      inFlightAuthTokenRequest = null;
    }
  } catch {
    if (pendingAuthTokenRequestId !== messageId) return;
    if (authTokenProvider !== provider) return;
    postAuthTokenUnavailableToIframe(iframe, origin, messageId);
    if (pendingAuthTokenRequestId === messageId) pendingAuthTokenRequestId = null;
    if (inFlightAuthTokenRequest?.messageId === messageId && inFlightAuthTokenRequest.provider === provider) {
      inFlightAuthTokenRequest = null;
    }
  }
}

function requestAuthTokenForIframe(iframe: HTMLIFrameElement, origin: string, messageId: string): void {
  if (
    pendingAuthTokenRequestId === messageId &&
    inFlightAuthTokenRequest?.messageId === messageId &&
    inFlightAuthTokenRequest.provider === authTokenProvider
  ) {
    return;
  }

  pendingAuthTokenRequestId = messageId;
  if (authTokenProvider) {
    inFlightAuthTokenRequest = { messageId, provider: authTokenProvider };
  } else {
    inFlightAuthTokenRequest = null;
  }
  void postAuthTokenToIframe(iframe, origin, messageId);
}

function requestFreshAuthTokenForMountedIframe(): void {
  if (!authTokenProvider || !mountedState) return;
  const iframe = document.getElementById("nest-support-widget-iframe");
  if (!(iframe instanceof HTMLIFrameElement)) return;
  requestAuthTokenForIframe(iframe, mountedState.origin, pendingAuthTokenRequestId ?? createMessageId());
}

function applyPanelSize(panel: HTMLDivElement, nextSize: PanelSize): void {
  panelSize = nextSize;
  writePanelSizeToStorage(panelSize);
  panel.dataset.size = panelSize;
}

function updateZoomButtonLabel(zoomButton: HTMLButtonElement, size: PanelSize): void {
  zoomButton.setAttribute("aria-label", size === "large" ? "縮小" : "拡大");
  zoomButton.title = size === "large" ? "縮小" : "拡大";
}

function setPanelOpenState(panel: HTMLDivElement, btn: HTMLButtonElement | null, open: boolean): void {
  const next = open ? "true" : "false";
  panel.dataset.open = next;
  panelOpen = open;
  postNotifications.refresh();

  if (btn) {
    btn.dataset.open = next;
    btn.setAttribute("aria-expanded", next === "true" ? "true" : "false");
    btn.setAttribute("aria-label", next === "true" ? "チャットを閉じる" : "サポートチャットを開く");
  }
}

function applyWidgetVisibility(btn: HTMLButtonElement | null, panel: HTMLDivElement, visible: boolean): void {
  const next = visible ? "true" : "false";
  if (btn) btn.dataset.visible = next;
  panel.dataset.visible = next;
}

function getWidgetState(): WidgetState {
  const panel = document.getElementById("nest-support-widget-panel");
  const open = panel instanceof HTMLDivElement ? panel.dataset.open === "true" : panelOpen;
  const unreadCount = (unreadState.initialized ? unreadState.lastUnreadCount : 0) + postUnreadCount;
  return { open, visible: widgetVisible, panelSize, unreadCount };
}

function postWidgetStateToIframe(state: WidgetState): void {
  /* c8 ignore next -- state request は mountedState 検証後にのみ処理し、destroy 時は送信フラグを先に戻す。 */
  if (!mountedState) return;
  const iframe = document.getElementById("nest-support-widget-iframe");
  if (!(iframe instanceof HTMLIFrameElement)) return;
  const message: WidgetStatePostMessage = {
    type: "nest-support-widget:state",
    ...state,
    postCloseGuard: true,
    postDialog: typeof HTMLDialogElement !== "undefined" && typeof HTMLDialogElement.prototype.showModal === "function",
  };
  try {
    iframe.contentWindow?.postMessage(message, mountedState.origin);
  } catch {
    // noop
  }
}

function emitWidgetState(): void {
  const state = getWidgetState();

  if (shouldPostWidgetStateToIframe) postWidgetStateToIframe(state);

  for (const listener of widgetStateListeners) {
    try {
      listener(state);
    } catch {
      // noop
    }
  }

  try {
    if (typeof CustomEvent !== "function") return;
    window.dispatchEvent(new CustomEvent(STATE_EVENT_NAME, { detail: state }));
  } catch {
    // noop
  }
}

function clearAnnouncementLayoutIntervention(): void {
  announcementBannerResizeObserver?.disconnect();
  announcementBannerResizeObserver = null;
  if (announcementHostResizeHandler) window.removeEventListener("resize", announcementHostResizeHandler);
  announcementHostResizeHandler = null;
  document.documentElement.removeAttribute(ANNOUNCEMENT_LAYOUT_ATTRIBUTE);
  document.documentElement.removeAttribute(ANNOUNCEMENT_POSITION_ATTRIBUTE);
  document.documentElement.style.removeProperty(ANNOUNCEMENT_OFFSET_PROPERTY);
  document.documentElement.style.removeProperty(ANNOUNCEMENT_HOST_PADDING_TOP_PROPERTY);
  document.documentElement.style.removeProperty(ANNOUNCEMENT_HOST_PADDING_BOTTOM_PROPERTY);
}

function syncInlineAnnouncementOffset(banner: HTMLDivElement): void {
  const height = Math.max(0, Math.ceil(banner.getBoundingClientRect().height));
  document.documentElement.style.setProperty(ANNOUNCEMENT_OFFSET_PROPERTY, `${height}px`);
}

function syncInlineAnnouncementLayout(banner: HTMLDivElement, position: AnnouncementPosition): void {
  document.documentElement.removeAttribute(ANNOUNCEMENT_LAYOUT_ATTRIBUTE);
  document.documentElement.removeAttribute(ANNOUNCEMENT_POSITION_ATTRIBUTE);
  const hostBodyStyle = window.getComputedStyle(document.body);
  document.documentElement.style.setProperty(ANNOUNCEMENT_HOST_PADDING_TOP_PROPERTY, hostBodyStyle.paddingTop || "0px");
  document.documentElement.style.setProperty(
    ANNOUNCEMENT_HOST_PADDING_BOTTOM_PROPERTY,
    hostBodyStyle.paddingBottom || "0px",
  );
  document.documentElement.setAttribute(ANNOUNCEMENT_LAYOUT_ATTRIBUTE, "inline");
  document.documentElement.setAttribute(ANNOUNCEMENT_POSITION_ATTRIBUTE, position);
  syncInlineAnnouncementOffset(banner);
}

function resolveAnnouncementCtaUrl(rawUrl: string): string | null {
  const value = rawUrl.trim();
  if (value.startsWith("/") && !value.startsWith("//") && !value.includes("\\")) {
    return new URL(value, window.location.origin).toString();
  }
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || url.username !== "" || url.password !== "") return null;
    return url.toString();
  } catch {
    return null;
  }
}

function removeAnnouncementFromCurrentQueue(announcement: WidgetAnnouncement): void {
  announcementState = announcementState.filter(
    (item) => item.id !== announcement.id || item.revision !== announcement.revision,
  );
  renderAnnouncement();
}

function completePendingAnnouncementCtaNavigation(requestId: string): void {
  if (pendingAnnouncementCtaNavigation?.requestId !== requestId) return;
  const { url, timeoutId } = pendingAnnouncementCtaNavigation;
  pendingAnnouncementCtaNavigation = null;
  window.clearTimeout(timeoutId);
  window.open(url, "_self");
}

function postAnnouncementCtaRequestToIframe(announcement: WidgetAnnouncement): string | null {
  /* c8 ignore next -- CTAはmountedState上のiframeから受信した告知にだけ描画される。 */
  if (!mountedState) return null;
  const iframe = document.getElementById("nest-support-widget-iframe");
  if (!(iframe instanceof HTMLIFrameElement)) return null;
  const requestId = createMessageId();
  try {
    iframe.contentWindow?.postMessage(
      {
        type: "nest-support-widget:announcement:cta:request",
        requestId,
        announcementId: announcement.id,
        revision: announcement.revision,
        ...(announcement.cta?.type === "start_chat" ? { action: "start_chat" } : {}),
      },
      mountedState.origin,
    );
    return requestId;
  } catch {
    return null;
  }
}

function activateAnnouncementCta(
  event: MouseEvent,
  announcement: WidgetAnnouncement,
  url: string,
): void {
  if (event.defaultPrevented || event.button !== (event.type === "click" ? 0 : 1)) return;
  const cta = announcement.cta;
  if (cta?.type !== "open_url" || !cta.dismissOnClick) return;

  removeAnnouncementFromCurrentQueue(announcement);
  const requestId = postAnnouncementCtaRequestToIframe(announcement);
  // 修飾キー・中クリックの開き方はブラウザーに任せ、通常の同一タブ遷移だけ保存を待つ。
  if (
    cta.openIn === "new_tab" || event.button === 1 ||
    event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
  ) return;

  event.preventDefault();
  if (!requestId) {
    window.open(url, "_self");
    return;
  }
  if (pendingAnnouncementCtaNavigation) {
    window.clearTimeout(pendingAnnouncementCtaNavigation.timeoutId);
  }
  const timeoutId = window.setTimeout(
    () => completePendingAnnouncementCtaNavigation(requestId),
    ANNOUNCEMENT_CTA_NAVIGATION_TIMEOUT_MS,
  );
  pendingAnnouncementCtaNavigation = { requestId, url, timeoutId };
}

function startAnnouncementChat(announcement: WidgetAnnouncement): void {
  if (pendingAnnouncementChat) return;
  announcementChatError = null;
  // ポストの自動表示で開いた場合も、会話への遷移後にパネルを閉じない。
  postPanelWasOpen = null;
  setOpen({ open: true });
  const requestId = postAnnouncementCtaRequestToIframe(announcement);
  if (requestId) pendingAnnouncementChat = { requestId, announcement };
  else announcementChatError = announcement;
  renderAnnouncement();
}

function completeAnnouncementChat(result: WidgetAnnouncementCtaResultPostMessage): void {
  if (!result.chat || pendingAnnouncementChat?.requestId !== result.requestId) return;
  const { announcement } = pendingAnnouncementChat;
  pendingAnnouncementChat = null;
  if (result.chat.status === "success" && result.chat.dismissed) removeAnnouncementFromCurrentQueue(announcement);
  if (result.chat.status === "error") announcementChatError = announcement;
  renderAnnouncement();
}

function renderAnnouncement(): void {
  clearAnnouncementLayoutIntervention();

  const existingBanner = announcementBannerElement;
  const announcement = announcementState[0];
  if (!widgetVisible || !announcement) {
    existingBanner?.remove();
    announcementBannerElement = null;
    return;
  }

  const banner =
    existingBanner ??
    createEl("div", { [ANNOUNCEMENT_BANNER_ATTRIBUTE]: "1" });
  announcementBannerElement = banner;
  banner.dataset.layout = announcement.layout;
  banner.dataset.position = announcement.position;
  banner.dataset.contentAlignment = announcement.contentAlignment;
  banner.dataset.dismissible = announcement.dismissible ? "true" : "false";
  banner.style.setProperty("--announcement-bg", announcement.style.backgroundColor);
  banner.style.setProperty("--announcement-fg", contrastingTextColor(announcement.style.backgroundColor));
  banner.style.setProperty("--announcement-button-bg", announcement.style.ctaButtonColor);
  banner.style.setProperty("--announcement-button-fg", contrastingTextColor(announcement.style.ctaButtonColor));
  banner.setAttribute("role", "status");
  banner.setAttribute("aria-live", "polite");
  banner.replaceChildren();

  const content = createEl("div", { "data-nest-support-announcement-content": "1" });
  const main = createEl("div", { "data-nest-support-announcement-main": "1" });
  const copy = createEl("div", { "data-nest-support-announcement-copy": "1" });
  const title = createEl("p", { "data-nest-support-announcement-title": "1" });
  title.textContent = announcement.title;
  const body = createEl("p", { "data-nest-support-announcement-body": "1" });
  body.textContent = announcement.body;
  copy.appendChild(title);
  if (announcement.body) copy.appendChild(body);
  main.appendChild(copy);

  if (announcement.cta?.type === "start_chat") {
    const cta = createEl("button", { type: "button", "data-nest-support-announcement-cta": "1", "data-appearance": announcement.cta.appearance });
    cta.textContent = pendingAnnouncementChat ? "チャットを開始中…" : announcement.cta.label;
    cta.disabled = pendingAnnouncementChat !== null;
    cta.setAttribute("aria-busy", String(cta.disabled));
    cta.addEventListener("click", () => startAnnouncementChat(announcement));
    main.appendChild(cta);
  } else if (announcement.cta) {
    const ctaUrl = resolveAnnouncementCtaUrl(announcement.cta.url);
    if (ctaUrl) {
      const cta = createEl("a", {
        href: ctaUrl,
        "data-nest-support-announcement-cta": "1",
        "data-appearance": announcement.cta.appearance,
      });
      cta.textContent = announcement.cta.label;
      if (announcement.cta.openIn === "new_tab") {
        cta.target = "_blank";
        cta.rel = "noopener noreferrer";
      }
      cta.addEventListener("click", (event) => activateAnnouncementCta(event, announcement, ctaUrl));
      if (Reflect.has(cta, "onauxclick")) {
        cta.addEventListener("auxclick", (event) => activateAnnouncementCta(event, announcement, ctaUrl));
      } else {
        // Safari 18.1以前では、同じリンク上で押して離した中クリックを補完する。
        let middleButtonDown = false;
        cta.addEventListener("mousedown", (event) => {
          middleButtonDown = event.button === 1 && !event.defaultPrevented;
        });
        cta.addEventListener("mouseleave", () => { middleButtonDown = false; });
        cta.addEventListener("mouseup", (event) => {
          const shouldActivate = middleButtonDown;
          middleButtonDown = false;
          if (!shouldActivate) return;
          // 既定のリンク遷移が処理される前にリンク要素を取り除かない。
          window.setTimeout(() => {
            if (cta.isConnected) activateAnnouncementCta(event, announcement, ctaUrl);
          }, 0);
        });
      }
      main.appendChild(cta);
    }
  }

  content.appendChild(main);
  if (announcementChatError?.id === announcement.id && announcementChatError.revision === announcement.revision) {
    const error = createEl("p", { role: "alert" });
    error.textContent = "チャットを開始できませんでした。再度お試しください。";
    content.appendChild(error);
  }

  if (announcement.dismissible) {
    const closeButton = createEl("button", {
      type: "button",
      "aria-label": "告知を閉じる",
      title: "閉じる",
      "data-nest-support-announcement-close": "1",
    });
    closeButton.textContent = "×";
    closeButton.addEventListener("click", () => {
      dismissAnnouncement(announcement);
    });
    content.appendChild(closeButton);
  }

  banner.appendChild(content);
  if (!document.body.contains(banner)) document.body.appendChild(banner);

  if (announcement.layout !== "inline") return;

  const syncLayout = () => syncInlineAnnouncementLayout(banner, announcement.position);
  syncLayout();
  announcementHostResizeHandler = syncLayout;
  window.addEventListener("resize", announcementHostResizeHandler);
  if (typeof ResizeObserver === "function") {
    announcementBannerResizeObserver = new ResizeObserver(syncLayout);
    announcementBannerResizeObserver.observe(banner);
  }
}

function postAnnouncementDismissRequestToIframe(options: WidgetAnnouncementDismissOptions): void {
  /* c8 ignore next -- dismissAnnouncement は mountedState 上の iframe から受信した告知にだけ到達する。 */
  if (!mountedState) return;
  const iframe = document.getElementById("nest-support-widget-iframe");
  if (!(iframe instanceof HTMLIFrameElement)) return;
  try {
    iframe.contentWindow?.postMessage(
      {
        type: "nest-support-widget:announcement:dismiss:request",
        announcementId: options.announcementId,
        revision: options.revision,
      },
      mountedState.origin,
    );
  } catch {
    // noop
  }
}

function parseOriginFromScriptSrc(script: HTMLScriptElement): string | null {
  try {
    const url = new URL(script.src);
    return url.origin;
  } catch {
    return null;
  }
}

function getSelfScript(): HTMLScriptElement | null {
  const byId = document.getElementById("nest-support-widget-script");
  if (byId instanceof HTMLScriptElement) return byId;

  const current = document.currentScript;
  const hasWidgetDataset = (s: HTMLScriptElement): boolean => {
    // clientId / clientName は汎用的な data 属性として他 script でも使われ得るため、
    // self 判定には origin / widgetPath のみを利用する。
    return Boolean(s.dataset.origin || s.dataset.widgetPath);
  };
  const isSelfBySrc = (s: HTMLScriptElement): boolean => {
    try {
      return /support-chat-widget\.js(\?.*)?$/.test(s.src);
    } catch {
      return false;
    }
  };

  if (current instanceof HTMLScriptElement) {
    if (isSelfBySrc(current) || hasWidgetDataset(current)) return current;
  }

  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>("script"));
  for (const s of scripts) {
    if (isSelfBySrc(s)) return s;
  }

  for (const s of scripts) {
    if (hasWidgetDataset(s)) return s;
  }

  return null;
}

function createEl<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    el.setAttribute(key, value);
  }
  return el;
}

function markAsWidgetLauncherButton(btn: HTMLButtonElement): void {
  btn.dataset.nestSupportWidgetLauncher = "1";
}

function isWidgetLauncherButton(btn: HTMLButtonElement): boolean {
  return btn.dataset.nestSupportWidgetLauncher === "1";
}

function getWidgetLauncherButton(): HTMLButtonElement | null {
  if (!mountedState) return null;
  if (mountedState.hideLauncher) return null;

  const btn = document.getElementById("nest-support-widget-btn");
  if (!(btn instanceof HTMLButtonElement)) return null;

  // CSS の作用対象を確定するため、念のためマーキングする
  if (!isWidgetLauncherButton(btn)) markAsWidgetLauncherButton(btn);
  return btn;
}

function ensureStyle(): void {
  if (document.getElementById("nest-support-widget-style")) return;

  const style = createEl("style", { id: "nest-support-widget-style" });
  style.textContent = `
  #nest-support-widget-btn[data-nest-support-widget-launcher="1"] {
    position: fixed;
    left: 16px;
    bottom: 16px;
    z-index: 2147483647;
    height: 56px;
    width: 56px;
    padding: 0;
    border-radius: 9999px;
	    border: none;
	    cursor: pointer;
	    background: #06b6d4;
	    color: #ffffff;
	    font: 600 14px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
	    box-shadow: 0 0 0 2px rgba(255,255,255,.95), 0 20px 50px rgba(0,0,0,.35);
	    overflow: visible;
	    display: flex;
	    align-items: center;
	    justify-content: flex-start;
	    gap: 0;
    white-space: nowrap;
    transition: width 180ms ease-out, padding 180ms ease-out, box-shadow 180ms ease-out;
  }
  #nest-support-widget-btn[data-nest-support-widget-launcher="1"]:hover {
    width: 260px;
    padding: 0 16px;
  }
  #nest-support-widget-btn[data-nest-support-widget-launcher="1"][data-open="true"] {
    background: #0891b2;
  }
  #nest-support-widget-btn[data-nest-support-widget-launcher="1"][data-visible="false"] {
    display: none !important;
  }
  #nest-support-widget-btn[data-nest-support-widget-launcher="1"] #nest-support-widget-btn-icon {
    position: relative;
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: 16px;
    top: 16px;
  }
  #nest-support-widget-btn[data-nest-support-widget-launcher="1"] #nest-support-widget-btn-label {
    opacity: 0;
    max-width: 0;
    overflow: hidden;
    margin-left: 56px;
    transform: translateX(-8px);
    transition: opacity 160ms ease-out, transform 160ms ease-out, max-width 180ms ease-out;
  }
  #nest-support-widget-btn[data-nest-support-widget-launcher="1"]:hover #nest-support-widget-btn-label {
    opacity: 1;
    max-width: calc(100% - 56px);
    transform: translateX(0);
  }
  #nest-support-widget-btn[data-nest-support-widget-launcher="1"] #nest-support-widget-badge {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9999px;
    background: #ef4444;
    color: #ffffff;
    font-size: 11px;
    font-weight: 800;
    line-height: 18px;
    white-space: nowrap;
    box-shadow: 0 0 0 2px #06b6d4, 0 2px 10px rgba(0,0,0,.25);
    display: none;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }
  #nest-support-widget-btn[data-nest-support-widget-launcher="1"][data-open="true"] #nest-support-widget-badge {
    display: none !important;
    box-shadow: 0 0 0 2px #0891b2, 0 2px 10px rgba(0,0,0,.25);
  }
  @keyframes nest-support-widget-badge-pulse {
    0% { transform: scale(1); }
    30% { transform: scale(1.35); }
    60% { transform: scale(1); }
    100% { transform: scale(1); }
  }
  #nest-support-widget-btn[data-nest-support-widget-launcher="1"] #nest-support-widget-badge.nest-support-widget-badge--pulse {
    animation: nest-support-widget-badge-pulse 520ms ease-out 0s 2;
  }
  @media (prefers-reduced-motion: reduce) {
    #nest-support-widget-btn[data-nest-support-widget-launcher="1"] #nest-support-widget-badge.nest-support-widget-badge--pulse {
      animation: none !important;
    }
  }
  #nest-support-widget-btn[data-nest-support-widget-launcher="1"] #nest-support-widget-icon-close { display: none; }
  #nest-support-widget-btn[data-nest-support-widget-launcher="1"][data-open="true"] #nest-support-widget-icon-chat { display: none; }
  #nest-support-widget-btn[data-nest-support-widget-launcher="1"][data-open="true"] #nest-support-widget-icon-close { display: block; }
  #nest-support-widget-btn[data-nest-support-widget-launcher="1"] #nest-support-widget-btn-label-open { display: none; }
	  #nest-support-widget-btn[data-nest-support-widget-launcher="1"][data-open="true"] #nest-support-widget-btn-label-closed { display: none; }
	  #nest-support-widget-btn[data-nest-support-widget-launcher="1"][data-open="true"] #nest-support-widget-btn-label-open { display: inline; }
		  #nest-support-widget-panel {
		    position: fixed;
		    left: 16px;
		    bottom: 80px;
		    z-index: 2147483647;
		    width: min(420px, calc(100vw - 72px));
		    height: min(696px, calc(100vh - 120px));
		    opacity: 0;
		    transform: translateY(10px);
		    pointer-events: none;
		    visibility: hidden;
		    overflow: visible;
		    transition: opacity 180ms ease-out, transform 180ms ease-out, width 180ms ease-out, height 180ms ease-out, visibility 0s linear 180ms;
		  }
		  #nest-support-widget-panel[data-size="large"] {
		    width: min(960px, calc(100vw - 72px));
		    height: calc(100vh - 96px);
		  }
		  #nest-support-widget-panel-inner {
		    width: 100%;
		    height: 100%;
		    background: #fff;
		    border-radius: 16px;
		    overflow: hidden;
		    box-shadow: 0 20px 50px rgba(0,0,0,.35);
		    border: 1px solid rgba(17,24,39,.08);
		  }
		  #nest-support-widget-panel[data-open="true"] {
		    opacity: 1;
		    transform: translateY(0);
		    pointer-events: auto;
	    visibility: visible;
		    transition: opacity 180ms ease-out, transform 180ms ease-out, width 180ms ease-out, height 180ms ease-out, visibility 0s linear 0s;
		  }
		  #nest-support-widget-panel[data-visible="false"] {
		    display: none !important;
		  }
		  #nest-support-widget-panel-btn-close,
		  #nest-support-widget-zoom-btn,
		  #nest-support-widget-panel-btn-settings {
		    position: absolute;
		    left: calc(100% + 8px);
		    width: 32px;
		    height: 32px;
		    padding: 0;
		    border-radius: 9999px;
		    border: 1px solid rgba(15,23,42,.18);
		    background: rgba(255,255,255,.98);
		    color: rgba(15,23,42,.92);
		    box-shadow: 0 20px 50px rgba(0,0,0,.25);
		    cursor: pointer;
		    display: none;
		    align-items: center;
		    justify-content: center;
		  }
		  #nest-support-widget-panel-btn-close { top: 0; }
		  #nest-support-widget-zoom-btn { top: 40px; }
		  #nest-support-widget-panel-btn-settings { top: 80px; }
		  #nest-support-widget-panel[data-open="true"] #nest-support-widget-panel-btn-close,
		  #nest-support-widget-panel[data-open="true"] #nest-support-widget-zoom-btn,
		  #nest-support-widget-panel[data-open="true"] #nest-support-widget-panel-btn-settings { display: flex; }
		  #nest-support-widget-panel-btn-close:hover,
		  #nest-support-widget-zoom-btn:hover,
		  #nest-support-widget-panel-btn-settings:hover { background: #fff; }
		  #nest-support-widget-panel-btn-close:disabled,
		  #nest-support-widget-zoom-btn:disabled,
		  #nest-support-widget-panel-btn-settings:disabled { opacity: .5; cursor: not-allowed; }
		  #nest-support-widget-panel-btn-close:focus-visible,
		  #nest-support-widget-zoom-btn:focus-visible,
		  #nest-support-widget-panel-btn-settings:focus-visible { outline: 2px solid #06b6d4; outline-offset: 2px; }
		  #nest-support-widget-panel-btn-close svg,
		  #nest-support-widget-zoom-btn svg,
		  #nest-support-widget-panel-btn-settings svg { width: 16px; height: 16px; }
		  #nest-support-widget-zoom-icon-min { display: none; }
		  #nest-support-widget-panel[data-size="large"] #nest-support-widget-zoom-icon-max { display: none; }
		  #nest-support-widget-panel[data-size="large"] #nest-support-widget-zoom-icon-min { display: block; }
  #nest-support-widget-iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR} {
    position: fixed;
    z-index: 2147483646;
    box-sizing: border-box;
    color: var(--announcement-fg);
    background: var(--announcement-bg);
    border-color: currentColor;
    font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    box-shadow: 0 8px 24px rgba(15, 23, 42, .14);
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR}[data-position="top"] { top: 0; }
  ${ANNOUNCEMENT_BANNER_SELECTOR}[data-position="bottom"] { bottom: 0; }
  ${ANNOUNCEMENT_BANNER_SELECTOR}[data-layout="inline"] {
    left: 0;
    right: 0;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR}[data-layout="inline"][data-position="top"] { border-bottom: 1px solid currentColor; }
  ${ANNOUNCEMENT_BANNER_SELECTOR}[data-layout="inline"][data-position="bottom"] { border-top: 1px solid currentColor; }
  ${ANNOUNCEMENT_BANNER_SELECTOR}[data-layout="floating"] {
    left: 12px;
    right: 12px;
    width: auto;
    max-width: 960px;
    margin: 12px auto;
    border: 1px solid currentColor;
    border-radius: 12px;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR} [data-nest-support-announcement-content="1"] {
    box-sizing: border-box;
    position: relative;
    width: 100%;
    max-width: 960px;
    margin: 0 auto;
    padding: 12px 16px;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR} [data-nest-support-announcement-main="1"] {
    box-sizing: border-box;
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR}[data-dismissible="true"] [data-nest-support-announcement-main="1"] {
    padding-right: 40px;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR}[data-content-alignment="left"] [data-nest-support-announcement-main="1"] {
    justify-content: flex-start;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR}[data-content-alignment="center"] [data-nest-support-announcement-main="1"] {
    justify-content: center;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR}[data-dismissible="true"][data-content-alignment="center"] [data-nest-support-announcement-main="1"] {
    padding-left: 40px;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR}[data-content-alignment="right"] [data-nest-support-announcement-main="1"] {
    justify-content: flex-end;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR} [data-nest-support-announcement-copy="1"] {
    min-width: 0;
    max-width: 100%;
    overflow-wrap: anywhere;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR}[data-content-alignment="left"] [data-nest-support-announcement-copy="1"] {
    text-align: left;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR}[data-content-alignment="center"] [data-nest-support-announcement-copy="1"] {
    text-align: center;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR}[data-content-alignment="right"] [data-nest-support-announcement-copy="1"] {
    text-align: right;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR} [data-nest-support-announcement-title="1"],
  ${ANNOUNCEMENT_BANNER_SELECTOR} [data-nest-support-announcement-body="1"] {
    margin: 0;
    padding: 0;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR} [data-nest-support-announcement-title="1"] {
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR} [data-nest-support-announcement-body="1"] {
    margin-top: 2px;
    color: inherit;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    white-space: pre-wrap;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR} [data-nest-support-announcement-cta="1"] {
    border: 0;
    background: none;
    padding: 0;
    font-family: inherit;
    cursor: pointer;
    flex: 0 0 auto;
    max-width: 100%;
    overflow-wrap: anywhere;
    box-sizing: border-box;
    color: inherit;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR} [data-nest-support-announcement-cta="1"][data-appearance="button"] {
    padding: 6px 12px;
    border-radius: 6px;
    color: var(--announcement-button-fg);
    background: var(--announcement-button-bg);
    text-decoration: none;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR} [data-nest-support-announcement-cta="1"]:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR} [data-nest-support-announcement-cta="1"]:disabled {
    cursor: wait;
    opacity: .7;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR} [data-nest-support-announcement-close="1"] {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    box-sizing: border-box;
    width: 28px;
    height: 28px;
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: 6px;
    color: inherit;
    background: transparent;
    cursor: pointer;
    font: 400 24px/26px ui-sans-serif, system-ui, sans-serif;
  }
  ${ANNOUNCEMENT_BANNER_SELECTOR} [data-nest-support-announcement-close="1"]:hover { opacity: .7; }
  ${ANNOUNCEMENT_BANNER_SELECTOR} [data-nest-support-announcement-close="1"]:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
  html[${ANNOUNCEMENT_LAYOUT_ATTRIBUTE}="inline"] body {
    box-sizing: border-box !important;
  }
  html[${ANNOUNCEMENT_LAYOUT_ATTRIBUTE}="inline"][${ANNOUNCEMENT_POSITION_ATTRIBUTE}="top"] body {
    padding-top: calc(var(${ANNOUNCEMENT_HOST_PADDING_TOP_PROPERTY}, 0px) + var(${ANNOUNCEMENT_OFFSET_PROPERTY}, 0px)) !important;
  }
  html[${ANNOUNCEMENT_LAYOUT_ATTRIBUTE}="inline"][${ANNOUNCEMENT_POSITION_ATTRIBUTE}="bottom"] body {
    padding-bottom: calc(var(${ANNOUNCEMENT_HOST_PADDING_BOTTOM_PROPERTY}, 0px) + var(${ANNOUNCEMENT_OFFSET_PROPERTY}, 0px)) !important;
  }
  html[${ANNOUNCEMENT_LAYOUT_ATTRIBUTE}="inline"] body > #app > [class~="h-screen"],
  html[${ANNOUNCEMENT_LAYOUT_ATTRIBUTE}="inline"] body > #root > [class~="h-screen"] {
    height: calc(100vh - var(${ANNOUNCEMENT_OFFSET_PROPERTY}, 0px)) !important;
    max-height: calc(100vh - var(${ANNOUNCEMENT_OFFSET_PROPERTY}, 0px)) !important;
  }
  @media (prefers-reduced-motion: reduce) {
    #nest-support-widget-btn[data-nest-support-widget-launcher="1"] { transition: none; }
    #nest-support-widget-btn[data-nest-support-widget-launcher="1"] #nest-support-widget-btn-label { transition: none; }
    #nest-support-widget-panel { transition: none; }
  }
  `;
  document.head.appendChild(style);
}

function ensureMessageListener(): void {
  if (isWindowMessageHandlerBound) return;

  windowMessageHandler = (event) => {
    if (!mountedState) return;
    if (postDialog.receive(event)) return;
    if (event.origin !== mountedState.origin) return;

	    const iframe = document.getElementById("nest-support-widget-iframe");
	    if (!(iframe instanceof HTMLIFrameElement)) return;
		  if (event.source !== iframe.contentWindow) return;

    if (event.data && typeof event.data === "object" && event.data.type === "nest-support-widget:embed-proof:request") {
      const messageId: unknown = event.data.messageId;
      if (typeof messageId !== "string" || !/^[a-zA-Z0-9-]{16,64}$/.test(messageId)) return;
      cancelEmbedProofRenewal?.();
      cancelEmbedProofRenewal = renewWidgetEmbedProof(iframe, mountedState.origin, messageId);
      return;
    }

    if (event.data?.type === "nest-support-widget:posts:dialog:refresh") {
      postDialog.refresh(event.data.clientId, event.data.sessionId);
      return;
    }
    if (event.data?.type === "nest-support-widget:posts:close:result") {
      const pending = widgetPostClose;
      if (pending && widgetPostGuard === pending.context && event.data.clientId === pending.context.clientId && event.data.postId === pending.context.postId && event.data.sessionId === pending.context.sessionId && event.data.requestId === pending.requestId) {
        widgetPostClose = null;
        if (event.data.allowed === true) pending.done();
      }
      return;
    }
    const postState = parsePostState(event.data);
    if (postState) {
      if (postState.clientId === (mountedState.clientId ?? null)) postNotifications.receive(postState);
      return;
    }
    if (event.data && typeof event.data === "object" && event.data.type === "nest-support-widget:posts:display") {
      const message = event.data as Record<string, unknown>;
      if (message.clientId !== mountedState.clientId) return;
      const panel = document.getElementById("nest-support-widget-panel");
      if (!(panel instanceof HTMLDivElement)) return;
      if (message.postId === null) {
        widgetPostGuard = null; widgetPostClose = null;
        postDialog.close();
        panel.dataset.size = panelSize;
        if (postPanelWasOpen === false) setPanelOpenState(panel, getWidgetLauncherButton(), false);
        postPanelWasOpen = null;
      } else if (widgetVisible && typeof message.postId === "string" && /^[a-zA-Z0-9_-]{1,100}$/.test(message.postId)) {
        if (message.presentation !== undefined && !isPostPresentation(message.presentation)) return;
        if (message.presentation === "dialog") {
          if (typeof message.sessionId !== "string" || !/^[a-zA-Z0-9_-]{1,100}$/.test(message.sessionId) || !mountedState.clientId) return;
          postNotifications.markOpened();
          postDialog.open({ origin: mountedState.origin, clientId: mountedState.clientId, postId: message.postId, sessionId: message.sessionId });
          postNotifications.refresh();
          return;
        }
        const size = isPostPresentation(message.presentation) ? legacyPostSize(message.presentation) : message.size;
        if (size !== "small" && size !== "large") return;
        widgetPostGuard = message.closeGuard === true && typeof message.sessionId === "string" && mountedState.clientId ? { clientId: mountedState.clientId, postId: message.postId, sessionId: message.sessionId } : null;
        widgetPostClose = null;
        postDialog.close();
        postNotifications.markOpened();
        if (postPanelWasOpen === null) postPanelWasOpen = panelOpen;
        panel.dataset.size = size === "large" ? "large" : "default";
        setPanelOpenState(panel, getWidgetLauncherButton(), true);
      }
      emitWidgetState();
      return;
    }

    const announcementsMessage = parseAnnouncementsPostMessage(event.data);
    if (announcementsMessage) {
      if (announcementsMessage.clientId !== (mountedState.clientId ?? null)) return;
      announcementState = announcementsMessage.announcements;
      renderAnnouncement();
      return;
    }

    const ctaResult = parseAnnouncementCtaResultPostMessage(event.data);
    if (ctaResult) {
      completeAnnouncementChat(ctaResult);
      completePendingAnnouncementCtaNavigation(ctaResult.requestId);
      return;
    }

    const authTokenRequest = parseAuthTokenRequestPostMessage(event.data);
    if (authTokenRequest) {
      requestAuthTokenForIframe(iframe, mountedState.origin, authTokenRequest.messageId);
      return;
    }

    const widgetStateRequest = parseWidgetStateRequestPostMessage(event.data);
    if (widgetStateRequest) {
      shouldPostWidgetStateToIframe = true;
      postWidgetStateToIframe(getWidgetState());
      return;
    }

	    const settingsRequest = parseSettingsRequestPostMessage(event.data);
	    if (settingsRequest) {
	      postSettingsToIframe(iframe, mountedState.origin);
      return;
    }

		    const settingsUpdate = parseSettingsUpdatePostMessage(event.data);
		    if (settingsUpdate) {
		      if (typeof settingsUpdate.soundEnabled === "boolean") {
		        soundEnabled = settingsUpdate.soundEnabled;
		        writeSoundEnabledToStorage(soundEnabled);
		        if (soundEnabled) bindAudioUnlockHandlers();
		      }
		      if (typeof settingsUpdate.soundVolume === "number") {
		        soundVolume = clampSoundVolume(settingsUpdate.soundVolume);
		        writeSoundVolumeToStorage(soundVolume);
		      }
		      if (settingsUpdate.panelSize) {
		        const panel = document.getElementById("nest-support-widget-panel");
		        if (panel instanceof HTMLDivElement) {
		          applyPanelSize(panel, settingsUpdate.panelSize);
		        }
		        const zoomBtn = document.getElementById("nest-support-widget-zoom-btn");
		        if (zoomBtn instanceof HTMLButtonElement) {
		          updateZoomButtonLabel(zoomBtn, panelSize);
		        }
            emitWidgetState();
		      }
		      postSettingsToIframe(iframe, mountedState.origin);
		      return;
		    }

    const soundTest = parseSoundTestPostMessage(event.data);
    if (soundTest) {
      playNotificationSound({ force: true });
      return;
    }

    const settingsDialogState = parseSettingsDialogStatePostMessage(event.data);
    if (settingsDialogState) {
      const btn = document.getElementById("nest-support-widget-panel-btn-settings");
      if (btn instanceof HTMLButtonElement) {
        btn.disabled = settingsDialogState.open;
        btn.setAttribute("aria-disabled", settingsDialogState.open ? "true" : "false");
      }
      return;
    }

    const msg = parseUnreadPostMessage(event.data);
    if (!msg) return;

    const normalized = Math.max(0, Math.floor(msg.unreadCount));
    updateBadge(normalized);

    if (!unreadState.initialized) {
      unreadState = { initialized: true, lastUnreadCount: normalized };
      emitWidgetState();
      return;
    }

    if (normalized > unreadState.lastUnreadCount) {
      const panel = document.getElementById("nest-support-widget-panel");
      const isOpen = panel instanceof HTMLDivElement && panel.dataset.open === "true";
      if (widgetVisible && !isOpen) pulseBadge();
      if (widgetVisible) playNotificationSound();
    }

    const changed = unreadState.lastUnreadCount !== normalized;
    unreadState.lastUnreadCount = normalized;
    if (changed) emitWidgetState();
  };

  window.addEventListener("message", windowMessageHandler);
  document.addEventListener("visibilitychange", refreshPostNotifications);
  document.addEventListener("focusout", refreshPostNotifications);
  isWindowMessageHandlerBound = true;

  if (soundEnabled) bindAudioUnlockHandlers();
}

function mount(options: Required<Pick<WidgetInitOptions, "origin" | "widgetPath" | "hideLauncher">> & WidgetInitOptions): void {
  ensureStyle();
  ensureMessageListener();

  const existingBtn = document.getElementById("nest-support-widget-btn");
  const hideLauncher = options.hideLauncher === true;
	  const existingPanel = document.getElementById("nest-support-widget-panel");
	  const existingPanelInner = document.getElementById("nest-support-widget-panel-inner");
	  const existingZoomBtn = document.getElementById("nest-support-widget-zoom-btn");
	  const existingCloseBtn = document.getElementById("nest-support-widget-panel-btn-close");
	  const existingSettingsBtn = document.getElementById("nest-support-widget-panel-btn-settings");
	  const existingIframe = document.getElementById("nest-support-widget-iframe");

  let btn: HTMLButtonElement | null = null;
  if (hideLauncher) {
    // host が同じ id の独自起動ボタンを持っている可能性があるため、
    // 削除対象は「ウィジェット自身が生成・管理している launcher」に限定する。
    if (existingBtn instanceof HTMLButtonElement && isWidgetLauncherButton(existingBtn)) existingBtn.remove();
  } else {
    btn = existingBtn instanceof HTMLButtonElement ? existingBtn : createEl("button", { id: "nest-support-widget-btn", type: "button" });
    markAsWidgetLauncherButton(btn);
    btn.setAttribute("aria-label", "サポートチャットを開く");

    if (!btn.dataset.nestSupportWidgetUiReady) {
      btn.dataset.nestSupportWidgetUiReady = "1";

      const iconWrap = createEl("span", { id: "nest-support-widget-btn-icon" });
      iconWrap.innerHTML = `
        <svg id="nest-support-widget-icon-chat" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
          <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"/>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
          <path d="M12 17h.01"/>
        </svg>
        <svg id="nest-support-widget-icon-close" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="10"/>
          <path d="m15 9-6 6"/>
          <path d="m9 9 6 6"/>
        </svg>
      `.trim();

      const label = createEl("span", { id: "nest-support-widget-btn-label" });
      const labelClosed = createEl("span", { id: "nest-support-widget-btn-label-closed" });
      labelClosed.textContent = "お気軽にご相談ください";
      const labelOpen = createEl("span", { id: "nest-support-widget-btn-label-open" });
      labelOpen.textContent = "チャットを閉じる";
      label.appendChild(labelClosed);
      label.appendChild(labelOpen);

      btn.textContent = "";
      btn.appendChild(iconWrap);
      btn.appendChild(label);
    }

    const existingBadge = document.getElementById("nest-support-widget-badge");
    const badge = existingBadge instanceof HTMLSpanElement ? existingBadge : createEl("span", { id: "nest-support-widget-badge" });
    const iconWrap = document.getElementById("nest-support-widget-btn-icon");
    if (iconWrap instanceof HTMLSpanElement) {
      if (!iconWrap.contains(badge)) iconWrap.appendChild(badge);
    } else {
      if (!btn.contains(badge)) btn.appendChild(badge);
    }
  }

	  const panel = existingPanel instanceof HTMLDivElement ? existingPanel : createEl("div", { id: "nest-support-widget-panel" });
	  const panelInner =
	    existingPanelInner instanceof HTMLDivElement ? existingPanelInner : createEl("div", { id: "nest-support-widget-panel-inner" });
	  const zoomBtn =
	    existingZoomBtn instanceof HTMLButtonElement ? existingZoomBtn : createEl("button", { id: "nest-support-widget-zoom-btn", type: "button" });
	  const closeBtn =
	    existingCloseBtn instanceof HTMLButtonElement ? existingCloseBtn : createEl("button", { id: "nest-support-widget-panel-btn-close", type: "button" });
	  const settingsBtn =
	    existingSettingsBtn instanceof HTMLButtonElement ? existingSettingsBtn : createEl("button", { id: "nest-support-widget-panel-btn-settings", type: "button" });
		  const iframe = existingIframe instanceof HTMLIFrameElement ? existingIframe : createEl("iframe", { id: "nest-support-widget-iframe", title: "Support Chat" });
  if (!iframe.dataset.nestSupportWidgetAuthLoadBound) {
    iframe.dataset.nestSupportWidgetAuthLoadBound = "1";
    iframe.addEventListener("load", () => {
      if (!mountedState) return;
      if (!authTokenProvider) return;
      requestAuthTokenForIframe(iframe, mountedState.origin, createMessageId());
    });
  }
	  // 旧実装では display を直指定していたため、移行時に data-open へ寄せる
  const displayOverride = panel.style.display;
  if (!panel.dataset.open) {
    panel.dataset.open = displayOverride === "block" ? "true" : "false";
  }
  if (displayOverride === "none" || displayOverride === "block") {
    panel.style.display = "";
  }

  panelOpen = panel.dataset.open === "true";
  if (btn) {
    btn.dataset.open = panel.dataset.open;
    btn.setAttribute("aria-expanded", panel.dataset.open === "true" ? "true" : "false");
    btn.setAttribute("aria-label", panel.dataset.open === "true" ? "チャットを閉じる" : "サポートチャットを開く");
  }
	  panel.dataset.size = panelSize;
	  updateZoomButtonLabel(zoomBtn, panelSize);
  applyWidgetVisibility(btn, panel, widgetVisible);
  if (!widgetVisible) {
    postDialog.close();
    setPanelOpenState(panel, btn, false);
    postSettingsDialogControlToIframe(iframe, options.origin, { type: "nest-support-widget:settings:dialog:close" });
  }

  closeBtn.setAttribute("aria-label", "ウィジェットを閉じる");
  closeBtn.title = "閉じる";
  if (!closeBtn.dataset.nestSupportWidgetUiReady) {
    closeBtn.dataset.nestSupportWidgetUiReady = "1";
    closeBtn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
        <path d="M18 6 6 18"/>
        <path d="m6 6 12 12"/>
      </svg>
    `.trim();
  }

  settingsBtn.setAttribute("aria-label", "設定");
  settingsBtn.title = "設定";
  if (!settingsBtn.dataset.nestSupportWidgetUiReady) {
    settingsBtn.dataset.nestSupportWidgetUiReady = "1";
    settingsBtn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
        <path d="M12 15.5A3.5 3.5 0 1 0 12 8.5a3.5 3.5 0 0 0 0 7Z"/>
        <path d="M19.4 15a1.8 1.8 0 0 0 .33 1.82l.06.06a2 2 0 0 1-1.41 3.41h-.16a2 2 0 0 1-1.41-.59l-.06-.06a1.8 1.8 0 0 0-1.82-.33 1.8 1.8 0 0 0-1.09 1.57V21a2 2 0 0 1-4 0v-.08a1.8 1.8 0 0 0-1.09-1.57 1.8 1.8 0 0 0-1.82.33l-.06.06a2 2 0 0 1-1.41.59h-.16a2 2 0 0 1-1.41-3.41l.06-.06A1.8 1.8 0 0 0 4.6 15a1.8 1.8 0 0 0-1.57-1.09H3a2 2 0 0 1 0-4h.08A1.8 1.8 0 0 0 4.65 8.3a1.8 1.8 0 0 0-.33-1.82l-.06-.06A2 2 0 0 1 5.67 3h.16a2 2 0 0 1 1.41.59l.06.06a1.8 1.8 0 0 0 1.82.33A1.8 1.8 0 0 0 10.21 2.4V2a2 2 0 0 1 3.58-1.23c.27.36.42.8.42 1.23v.4a1.8 1.8 0 0 0 1.09 1.57 1.8 1.8 0 0 0 1.82-.33l.06-.06A2 2 0 0 1 19 5.67v.16a2 2 0 0 1-.59 1.41l-.06.06a1.8 1.8 0 0 0-.33 1.82 1.8 1.8 0 0 0 1.57 1.09H21a2 2 0 0 1 0 4h-.08A1.8 1.8 0 0 0 19.4 15Z"/>
      </svg>
    `.trim();
  }

  if (mountedState && mountedState.clientId !== options.clientId) {
    // 顧客が切り替わった場合は未読状態をリセットする（前の cid の未読で鳴らさない）
    unreadState = { initialized: false, lastUnreadCount: 0 };
    updateBadge(0);
    emitWidgetState();
  }

  const url = new URL(options.widgetPath, options.origin);
  if (options.clientId) url.searchParams.set("cid", options.clientId);
  if (options.clientName) url.searchParams.set("clientName", options.clientName);
  const parentHost = window.location.hostname.trim().toLowerCase();
  if (parentHost) url.searchParams.set("parentHost", parentHost);
  const nextIframeUrl = url.toString();
  // 埋め込み先のブラウザ戻る履歴を汚染しないため、可能なら iframe 内の履歴を「追加」ではなく「置換」する
  const hasSameUrlAsMountedState =
    iframe === existingIframe &&
    mountedState?.origin === options.origin &&
    mountedState?.widgetPath === options.widgetPath &&
    mountedState?.clientId === options.clientId &&
    mountedState?.clientName === options.clientName;

  if (!hasSameUrlAsMountedState) {
    const frameWindow = iframe.contentWindow;
    if (frameWindow) {
      try {
        frameWindow.location.replace(nextIframeUrl);
      } catch {
        iframe.src = nextIframeUrl;
      }
    } else {
      iframe.src = nextIframeUrl;
    }
  }

	  if (!panel.contains(panelInner)) panel.appendChild(panelInner);
	  if (panel.contains(iframe) && iframe.parentElement === panel) {
	    panel.removeChild(iframe);
	  }
	  if (!panelInner.contains(iframe)) panelInner.appendChild(iframe);

	  if (!zoomBtn.dataset.nestSupportWidgetUiReady) {
	    zoomBtn.dataset.nestSupportWidgetUiReady = "1";
	    zoomBtn.innerHTML = `
	      <svg id="nest-support-widget-zoom-icon-max" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
	        <polyline points="15 3 21 3 21 9"/>
	        <polyline points="9 21 3 21 3 15"/>
	        <line x1="21" y1="3" x2="14" y2="10"/>
	        <line x1="3" y1="21" x2="10" y2="14"/>
	      </svg>
	      <svg id="nest-support-widget-zoom-icon-min" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
	        <polyline points="4 14 10 14 10 20"/>
	        <polyline points="20 10 14 10 14 4"/>
	        <line x1="14" y1="10" x2="21" y2="3"/>
	        <line x1="3" y1="21" x2="10" y2="14"/>
	      </svg>
	    `.trim();
	  }

	  if (!zoomBtn.dataset.nestSupportWidgetBound) {
	    zoomBtn.dataset.nestSupportWidgetBound = "1";
	    zoomBtn.addEventListener("click", () => {
	      const panel = document.getElementById("nest-support-widget-panel");
	      if (!(panel instanceof HTMLDivElement)) return;
	      const iframe = document.getElementById("nest-support-widget-iframe");
	      if (!(iframe instanceof HTMLIFrameElement)) return;
	      if (!mountedState) return;

	      applyPanelSize(panel, panelSize === "large" ? "default" : "large");
	      updateZoomButtonLabel(zoomBtn, panelSize);
	      postSettingsToIframe(iframe, mountedState.origin);
        emitWidgetState();
	    });
	  }

	  if (!closeBtn.dataset.nestSupportWidgetBound) {
	    closeBtn.dataset.nestSupportWidgetBound = "1";
	    closeBtn.addEventListener("click", () => {
        if (requestWidgetPostClose(() => {
          const currentPanel = document.getElementById("nest-support-widget-panel");
          if (currentPanel instanceof HTMLDivElement) { postPanelWasOpen = false; setPanelOpenState(currentPanel, getWidgetLauncherButton(), false); emitWidgetState(); }
        })) return;
	      const panel = document.getElementById("nest-support-widget-panel");
	      if (!(panel instanceof HTMLDivElement)) return;
        const launcherBtn = getWidgetLauncherButton();
	      const iframe = document.getElementById("nest-support-widget-iframe");
	      if (iframe instanceof HTMLIFrameElement && mountedState) {
	        postSettingsDialogControlToIframe(iframe, mountedState.origin, { type: "nest-support-widget:settings:dialog:close" });
	      }
	      setPanelOpenState(panel, launcherBtn, false);
        emitWidgetState();
	    });
	  }

	  if (!settingsBtn.dataset.nestSupportWidgetBound) {
	    settingsBtn.dataset.nestSupportWidgetBound = "1";
	    settingsBtn.addEventListener("click", () => {
	      const iframe = document.getElementById("nest-support-widget-iframe");
	      if (!(iframe instanceof HTMLIFrameElement)) return;
	      if (!mountedState) return;
	      postSettingsDialogControlToIframe(iframe, mountedState.origin, { type: "nest-support-widget:settings:dialog:open" });
	    });
	  }

		  // 表示順（Tab 順）を固定する
		  for (const el of [closeBtn, zoomBtn, settingsBtn]) {
		    if (panel.contains(el)) panel.removeChild(el);
		  }
		  panel.append(closeBtn, zoomBtn, settingsBtn);

  if (btn) {
    if (!btn.dataset.nestSupportWidgetBound) {
      btn.dataset.nestSupportWidgetBound = "1";
      btn.addEventListener("click", () => {
        if (!widgetVisible) return;
        const isOpen = panel.dataset.open === "true";
        if (isOpen && requestWidgetPostClose(() => { postPanelWasOpen = false; setPanelOpenState(panel, btn, false); emitWidgetState(); })) return;
        if (isOpen) {
          const iframe = document.getElementById("nest-support-widget-iframe");
          if (iframe instanceof HTMLIFrameElement && mountedState) {
            postSettingsDialogControlToIframe(iframe, mountedState.origin, { type: "nest-support-widget:settings:dialog:close" });
          }
        }
        setPanelOpenState(panel, btn, !isOpen);
        if (!isOpen) requestFreshAuthTokenForMountedIframe();
        emitWidgetState();
      });
    }

    if (!document.body.contains(btn)) document.body.appendChild(btn);
  }
  if (!document.body.contains(panel)) document.body.appendChild(panel);

  mountedState = {
    origin: options.origin,
    widgetPath: options.widgetPath,
    clientId: options.clientId,
    clientName: options.clientName,
    hideLauncher,
  };

  emitWidgetState();
}

function init(options: WidgetInitOptions = {}): void {
  assertAllowedHost();

  const script = getSelfScript();
  const originFromScript = script ? parseOriginFromScriptSrc(script) : null;

  const originRaw = options.origin ?? script?.dataset.origin ?? originFromScript;
  if (!originRaw) {
    throw new Error(
      'NestSupportWidget: origin が特定できません（init({ origin }) または <script data-origin="https://..."> を指定してください）',
    );
  }

  const origin = normalizeOrigin(originRaw);
  if (!origin) {
    throw new Error(
      `NestSupportWidget: origin が不正です（init({ origin }) / data-origin には "https://example.com" の形式で指定してください）: ${originRaw}`,
    );
  }

  const widgetPath = options.widgetPath ?? script?.dataset.widgetPath ?? DEFAULT_WIDGET_PATH;
  const clientId = normalizeOptionalString(options.clientId ?? script?.dataset.clientId);
  const clientNameRaw = normalizeOptionalString(options.clientName ?? script?.dataset.clientName);
  const clientName = clientId ? clientNameRaw : undefined;

  const hideLauncherFromScript = parseOptionalBoolean(script?.dataset.hideLauncher) ?? false;
  const hideLauncher = typeof options.hideLauncher === "boolean" ? options.hideLauncher : hideLauncherFromScript;

  mount({ origin, widgetPath, clientId, clientName, hideLauncher });
}

function setClient(options: WidgetSetClientOptions = {}): void {
  if (!mountedState) {
    // 埋め込み先（ホスト）の UI を落とさないため、init の設定不備は握りつぶす。
    try {
      init({ clientId: options.clientId, clientName: options.clientName });
    } catch {
      // noop
    }
    return;
  }

  const has = (k: string) => Object.prototype.hasOwnProperty.call(options, k);
  const nextClientId = has("clientId") ? options.clientId : mountedState.clientId;
  const nextClientName = has("clientName") ? options.clientName : mountedState.clientName;

  const normalizedClientId = typeof nextClientId === "string" && nextClientId.trim().length > 0 ? nextClientId : undefined;
  const normalizedClientName =
    normalizedClientId && typeof nextClientName === "string" && nextClientName.trim().length > 0 ? nextClientName : undefined;

  if (normalizedClientId !== mountedState.clientId) {
    postDialog.close();
    widgetPostGuard = null; widgetPostClose = null;
    postNotifications.reset();
    postPanelWasOpen = null;
    announcementState = [];
    renderAnnouncement();
  }

  mount({
    origin: mountedState.origin,
    widgetPath: mountedState.widgetPath,
    clientId: normalizedClientId,
    clientName: normalizedClientName,
    hideLauncher: mountedState.hideLauncher,
  });
}

function setAuthTokenProvider(provider?: WidgetAuthTokenProvider | null): void {
  if (provider !== authTokenProvider) postDialog.close();
  authTokenProvider = typeof provider === "function" ? provider : null;
  if (!authTokenProvider) {
    pendingAuthTokenRequestId = null;
    inFlightAuthTokenRequest = null;
  }
  if (!authTokenProvider || !mountedState) return;
  const iframe = document.getElementById("nest-support-widget-iframe");
  if (!(iframe instanceof HTMLIFrameElement)) return;
  requestAuthTokenForIframe(iframe, mountedState.origin, pendingAuthTokenRequestId ?? createMessageId());
}

function setOpen(options: WidgetSetOpenOptions = {}): void {
  const hasOpen = Object.prototype.hasOwnProperty.call(options, "open");
  if (!hasOpen) return;
  if (typeof options.open !== "boolean") return;

  if (!mountedState) {
    // 埋め込み先（ホスト）の UI を落とさないため、init の設定不備は握りつぶす。
    try {
      init();
    } catch {
      // noop
    }
    if (!mountedState) return;
  }

  if (options.open && !widgetVisible) return;

  const panel = document.getElementById("nest-support-widget-panel");
  if (!(panel instanceof HTMLDivElement)) return;
  const launcherBtn = getWidgetLauncherButton();

  const isOpen = panel.dataset.open === "true";
  if (isOpen === options.open) return;

  if (!options.open) {
    const iframe = document.getElementById("nest-support-widget-iframe");
    if (iframe instanceof HTMLIFrameElement && mountedState) {
      postSettingsDialogControlToIframe(iframe, mountedState.origin, { type: "nest-support-widget:settings:dialog:close" });
    }
  }

  setPanelOpenState(panel, launcherBtn, options.open);
  if (options.open) requestFreshAuthTokenForMountedIframe();
  emitWidgetState();
}

function getState(): WidgetState {
  return getWidgetState();
}

function onStateChange(listener: WidgetStateListener): () => void {
  widgetStateListeners.add(listener);
  try {
    listener(getWidgetState());
  } catch {
    // noop
  }
  return () => {
    widgetStateListeners.delete(listener);
  };
}

function dismissAnnouncement(announcementToDismiss: WidgetAnnouncement): void {
  removeAnnouncementFromCurrentQueue(announcementToDismiss);
  postAnnouncementDismissRequestToIframe({
    announcementId: announcementToDismiss.id,
    revision: announcementToDismiss.revision,
  });
}

function setVisibility(options: WidgetSetVisibilityOptions = {}): void {
  const hasVisible = Object.prototype.hasOwnProperty.call(options, "visible");
  if (!hasVisible) return;
  if (typeof options.visible !== "boolean") return;
  widgetVisible = options.visible;
  postNotifications.refresh();
  renderAnnouncement();

  const panel = document.getElementById("nest-support-widget-panel");
  if (!(panel instanceof HTMLDivElement)) return;
  const launcherBtn = getWidgetLauncherButton();

  if (!widgetVisible) {
    postDialog.close();
    setPanelOpenState(panel, launcherBtn, false);
    const iframe = document.getElementById("nest-support-widget-iframe");
    if (iframe instanceof HTMLIFrameElement && mountedState) {
      postSettingsDialogControlToIframe(iframe, mountedState.origin, { type: "nest-support-widget:settings:dialog:close" });
    }
  }

  applyWidgetVisibility(launcherBtn, panel, widgetVisible);
  emitWidgetState();
}

function destroy(): void {
  cancelEmbedProofRenewal?.();
  cancelEmbedProofRenewal = null;
  postDialog.close();
  widgetPostGuard = null; widgetPostClose = null;
  postNotifications.reset();
  postPanelWasOpen = null;
  document.removeEventListener("visibilitychange", refreshPostNotifications);
  document.removeEventListener("focusout", refreshPostNotifications);
  const btn = document.getElementById("nest-support-widget-btn");
  // host 側の独自起動ボタンを消さないよう、ウィジェット自身が管理している launcher のみ削除する
  if (btn instanceof HTMLButtonElement && isWidgetLauncherButton(btn)) btn.remove();
  document.getElementById("nest-support-widget-panel")?.remove();
  announcementBannerElement?.remove();
  announcementBannerElement = null;
  if (pendingAnnouncementCtaNavigation) {
    window.clearTimeout(pendingAnnouncementCtaNavigation.timeoutId);
    pendingAnnouncementCtaNavigation = null;
  }
  pendingAnnouncementChat = null;
  announcementChatError = null;
  clearAnnouncementLayoutIntervention();
  document.getElementById("nest-support-widget-style")?.remove();

  if (windowMessageHandler) {
    window.removeEventListener("message", windowMessageHandler);
    windowMessageHandler = null;
    isWindowMessageHandlerBound = false;
  }

	  mountedState = null;
  shouldPostWidgetStateToIframe = false;
  authTokenProvider = null;
  pendingAuthTokenRequestId = null;
  inFlightAuthTokenRequest = null;
	  unreadState = { initialized: false, lastUnreadCount: 0 };
  announcementState = [];
  widgetVisible = true;
  panelOpen = false;
  emitWidgetState();
}

declare global {
  interface Window {
    NestSupportWidget?: {
	      init: (options?: WidgetInitOptions) => void;
	      setClient: (options?: WidgetSetClientOptions) => void;
      setAuthTokenProvider: (provider?: WidgetAuthTokenProvider | null) => void;
	      setOpen: (options?: WidgetSetOpenOptions) => void;
      setVisibility: (options?: WidgetSetVisibilityOptions) => void;
      getState: () => WidgetState;
      onStateChange: (listener: WidgetStateListener) => () => void;
      destroy: () => void;
    };
  }
}

window.NestSupportWidget = {
  init,
  setClient,
  setAuthTokenProvider,
  setOpen,
  setVisibility,
  getState,
  onStateChange,
  destroy,
};
// data 属性だけで動かしたいので自動 init
try {
  init();
} catch {
  // noop: 明示 init する運用もあるため
}

export {};
